import { hasNonEmptyValue } from 'Lib/Any';
import React, { useEffect, useRef } from 'react';

/** Hook to return if a value changed */
export const useHasChanged = <T>(value: T): boolean | undefined => {
  return usePrevious(value) !== value;
};

/** Hook to obtain the previous value */
export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

/**
 * The original function would initialise the ref.current with undefined and as such would be considered changed
 * when the associated property is given a value.
 * This could occur at anytime so it could be considered true if it changes during the loading process,
 * due to user interaction or if the initial value of the associated property is not undefined i.e. it is considered changes straight away.
 * We needed an alternative to that where we initialise ref.current to the initial value of an associated property
 * which would never start as null.
 * So essentially we are only caring about whether the value has changed AFTER the whole first load is complete
 * i.e. has changed due to user interaction and not has changed during the loading process OR user interaction.
 */
export const useHasChangedAfterFirstLoad = <T>(value: T): boolean | undefined => {
  return usePreviousAfterFirstLoad(value) !== value;
};

/** Hook to obtain the previous value */
export const usePreviousAfterFirstLoad = <T>(value: T): T | undefined => {
  const ref = useRef<T>(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

/**
 * Hook that allows state to persist beyond the life page/tab. At times, this sort of state is saved as user data, but often there is so much
 * configuration that saving it against the user becomes superfluous.
 * A few examples:
 *    - A calendar app where we want to save the default view to be the "current week"
 *    - A currency option for data being displayed.
 *    - A default camera to be used when accessing video devices
 *
 * This hook was copied from: https://www.joshwcomeau.com/react/persisting-react-state-in-localstorage/
 */
export function useLocalStorageState(defaultValue: any, key: string) {
  const [value, setValue] = React.useState(() => {
    const stickyValue = window.localStorage.getItem(key);
    try {
      return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  });
  React.useEffect(() => {
    if (hasNonEmptyValue(value)) window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

export { usePromptOnNavigate } from './usePromptOnNavigate';
export { useSerialPortDisconnectListener } from './useSerialPortDisconnectListener';
export { useUrlSearchParams } from './useUrlSearchParams';
